import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import {
  Box,
  MenuItem,
  TextField,
  Select,
  FormControl,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { brainTokens } from "../design/brainTheme";
import {
  getProjectJiraTransitions,
  listJiraAssignableUsers,
  projectJiraAction,
  updateProjectAction,
  type JiraAssignableUser,
  type JiraTransitionOption,
  type ProjectAction,
} from "../lib/brain-api";
import { CLIENT_STATUS_LANES } from "../pages/mui/actionGrouping";
import { PersonAvatar } from "./PersonAvatar";
import {
  deriveActionStatus,
  groupActionsByWorkStream,
  JIRA_BROWSE,
  jiraTransitionOptions,
  parseJiraKey,
} from "./actionRegisterStatus";
import { AddWorkBar, type AddWorkCreated } from "./AddWorkBar";
import { Segmented } from "./Segmented";
import type { PillTone } from "./StatusPill";
import { AssigneeMonogram, StatusDot } from "./RegisterTable";

const c = brainTokens.color;

export interface ActionRegisterPerson {
  userId: string;
  displayName: string;
}

const STATUS_OPTIONS = ["Not raised", "Raised", ...CLIENT_STATUS_LANES, "Dismiss"] as const;

/** Same visual language as standup "Today's priorities" / ScoreBadge. */
function priorityLook(priority?: string): { fg: string; bg: string; spine: string; label: string } {
  const label = (priority || "P3").toUpperCase();
  if (label === "P1") return { fg: c.dangerText, bg: c.dangerBg, spine: c.brandRed, label };
  if (label === "P2") return { fg: c.warnText, bg: c.warnBg, spine: c.warnDot, label };
  return { fg: c.muted, bg: c.neutralPillBg, spine: c.border, label: label || "P3" };
}

function statusDotColor(tone: PillTone): string {
  if (tone === "success") return c.successDot;
  if (tone === "warning") return c.warnDot;
  if (tone === "danger") return c.dangerText;
  if (tone === "teal") return c.teal;
  return c.faint;
}

const ROW_GRID =
  "20px 78px 112px minmax(0,1fr) 160px 32px 100px";
const ROW_GRID_PROJECT =
  "20px 78px 100px minmax(0,1fr) 90px 150px 32px 90px";

function applyStatusChoice(
  a: ProjectAction,
  choice: string,
): Parameters<typeof updateProjectAction>[1] {
  if (choice === "Dismiss") {
    return { status: "dismissed" };
  }
  if (choice === "Done") {
    return { status: "done", clientStatus: "Done" };
  }
  if (choice === "Not raised") {
    return {
      status: a.status === "done" || a.status === "dismissed" ? "captured" : a.status,
      clientStatus: null,
    };
  }
  if (choice === "Raised") {
    return {
      status: a.status === "done" || a.status === "dismissed" ? "captured" : a.status,
      clientStatus: null,
    };
  }
  return {
    status: a.status === "done" || a.status === "dismissed" ? "captured" : a.status,
    clientStatus: choice,
  };
}

function PriorityBadge({ priority }: { priority?: string }) {
  const s = priorityLook(priority);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flex: "none",
        background: s.bg,
        color: s.fg,
        borderRadius: "7px",
        px: "8px",
        py: "4px",
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: "0.02em",
        fontFamily: brainTokens.font.sans,
      }}
    >
      {s.label}
    </Box>
  );
}

function ColHeader({ showProjectCol }: { showProjectCol: boolean }) {
  const cols = showProjectCol
    ? ["", "Priority", "Ticket", "Summary", "Project", "Status", "", "Ref"]
    : ["", "Priority", "Ticket", "Summary", "Status", "", "Ref"];
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: showProjectCol ? ROW_GRID_PROJECT : ROW_GRID,
        gap: "14px",
        alignItems: "center",
        px: 1.5,
        pb: 1,
        borderBottom: `1px solid ${c.borderLight2}`,
        mb: "2px",
        "& > span": {
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.09em",
          color: c.faint,
          textTransform: "uppercase",
          fontFamily: brainTokens.font.sans,
        },
        "& .end": { textAlign: "right" },
        "@media (max-width: 900px)": {
          gridTemplateColumns: "20px 74px 100px minmax(0,1fr) 32px 92px",
          "& .col-status, & .col-project": { display: "none" },
        },
      }}
    >
      {cols.map((label, i) => (
        <Box
          component="span"
          key={`${label}-${i}`}
          className={
            label === "Status" ? "col-status" : label === "Project" ? "col-project" : label === "Ref" ? "end" : undefined
          }
        >
          {label}
        </Box>
      ))}
    </Box>
  );
}

export function InlineSelect({
  value,
  options,
  onChange,
  renderValue,
  width = 120,
  onOpen,
  disabled,
}: {
  value: string;
  options: { value: string; label: string; disabled?: boolean }[];
  onChange: (v: string) => void;
  renderValue?: (v: string) => ReactNode;
  width?: number;
  onOpen?: () => void;
  disabled?: boolean;
}) {
  return (
    <FormControl size="small" sx={{ minWidth: width }} onClick={(e) => e.stopPropagation()}>
      <Select
        value={value}
        displayEmpty
        disabled={disabled}
        onOpen={onOpen}
        onChange={(e) => {
          const next = String(e.target.value);
          if (!next || next.startsWith("__")) return;
          onChange(next);
        }}
        renderValue={(v) => (renderValue ? renderValue(String(v)) : String(v) || "—")}
        sx={{
          fontSize: 12.5,
          "& .MuiSelect-select": { py: 0.35, px: 0.75 },
          "& fieldset": { borderColor: "transparent" },
          background: "transparent",
          "&:hover fieldset": { borderColor: c.border },
        }}
      >
        {options.map((o) => (
          <MenuItem key={o.value || "empty"} value={o.value} disabled={o.disabled}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/**
 * Jira-backed register assignee: the monogram IS the control. Reassigning is
 * the other half of "drive the board from the brain" — the status dropdown
 * already writes through, and having to open Jira just to change who owns a
 * ticket is the step that sends people back to Jira for everything else.
 * Loads assignable users on open so the list is never stale.
 */
export function JiraAssigneeSelect({
  projectId,
  issueKey,
  assignee,
  onOptimistic,
  onRevert,
}: {
  projectId: string;
  issueKey: string;
  assignee: string | null;
  onOptimistic: (next: { assignee: string | null; assigneeAccountId: string | null }) => void;
  onRevert: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [users, setUsers] = useState<JiraAssignableUser[] | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (users) return;
    void listJiraAssignableUsers()
      .then(setUsers)
      .catch(() => setUsers([]));
  };

  const choose = async (accountId: string) => {
    if (busy) return;
    // "__none" is the explicit Unassign choice; Jira takes null for that.
    const id = accountId === "__none" ? null : accountId;
    const name = id ? (users ?? []).find((u) => u.accountId === id)?.displayName ?? null : null;
    onOptimistic({ assignee: name, assigneeAccountId: id });
    setBusy(true);
    try {
      await projectJiraAction(projectId, issueKey, {
        action: "assign",
        accountId: id,
        ...(name ? { assigneeName: name } : {}),
      });
      enqueueSnackbar(`${issueKey} → ${name ?? "Unassigned"}`, { variant: "success" });
    } catch (e) {
      onRevert();
      enqueueSnackbar(e instanceof Error ? e.message : "Reassign failed.", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <InlineSelect
      value=""
      width={50}
      disabled={busy}
      onOpen={load}
      options={[
        { value: "__none", label: "Unassigned" },
        ...(users === null
          ? [{ value: "__loading", label: "Loading…", disabled: true }]
          : users.map((u) => ({ value: u.accountId, label: u.displayName }))),
      ]}
      renderValue={() => <AssigneeMonogram name={assignee} />}
      onChange={(id) => void choose(id)}
    />
  );
}

/**
 * Jira-backed register status: InlineSelect that loads transitions on open,
 * then writes through via projectJiraAction with optimistic status updates.
 */
export function JiraTransitionSelect({
  projectId,
  issueKey,
  statusLabel,
  statusTone,
  onOptimistic,
  onRevert,
}: {
  projectId: string;
  issueKey: string;
  statusLabel: string;
  statusTone: PillTone;
  onOptimistic: (next: { status: string; statusCategory?: string }) => void;
  onRevert: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [transitions, setTransitions] = useState<JiraTransitionOption[] | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setTransitions(null);
    void getProjectJiraTransitions(projectId, issueKey)
      .then(setTransitions)
      .catch(() => setTransitions([]));
  };

  const choose = async (transitionId: string) => {
    const t = (transitions ?? []).find((x) => x.id === transitionId);
    if (!t || busy) return;
    onOptimistic({ status: t.toStatus, statusCategory: t.toStatusCategory });
    setBusy(true);
    try {
      await projectJiraAction(projectId, issueKey, {
        action: "transition",
        transitionId: t.id,
        toStatus: t.toStatus,
      });
      enqueueSnackbar(`${issueKey} → ${t.toStatus}`, { variant: "success" });
      setTransitions(null);
    } catch (e) {
      onRevert();
      enqueueSnackbar(e instanceof Error ? e.message : "Transition failed.", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <InlineSelect
      value=""
      width={150}
      disabled={busy}
      options={jiraTransitionOptions(statusLabel, transitions)}
      onOpen={load}
      renderValue={() => <StatusDot label={statusLabel} tone={statusTone} />}
      onChange={(id) => void choose(id)}
    />
  );
}

function WorkStreamCard({
  stream,
  rows,
  startIndex,
  defaultOpen,
  blocked,
  people,
  canEdit,
  onPatch,
  showProjectCol,
}: {
  stream: string;
  rows: ProjectAction[];
  startIndex: number;
  defaultOpen: boolean;
  blocked: boolean;
  people: ActionRegisterPerson[];
  canEdit: boolean;
  onPatch: (id: string, patch: Parameters<typeof updateProjectAction>[1], optimistic: Partial<ProjectAction>) => void;
  showProjectCol: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const openCount = rows.filter((a) => a.status !== "done" && a.status !== "dismissed").length;
  const label = stream === "(ungrouped)" ? "Ungrouped" : stream;
  const assigneeOpts = [
    { value: "", label: "—" },
    ...people.map((p) => ({ value: p.displayName, label: p.displayName })),
  ];
  for (const a of rows) {
    const who = a.assignee?.trim();
    if (who && !assigneeOpts.some((o) => o.value === who)) assigneeOpts.push({ value: who, label: who });
  }

  const toggle = () => setOpen((v) => !v);

  return (
    <Box
      sx={{
        background: c.pageBg,
        border: `1px solid ${c.border}`,
        borderRadius: `${brainTokens.radius.card}px`,
        p: "20px 23px",
        boxShadow: "0 1px 2px rgba(20,40,38,.04)",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.75,
          mb: open ? 1.75 : 0,
          flexWrap: "wrap",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            fontSize: 16,
            fontWeight: 700,
            color: c.ink,
            fontFamily: brainTokens.font.sans,
            "& .ti": { fontSize: 18, color: c.muted },
          }}
        >
          <i className="ti ti-list-numbers" aria-hidden />
          {label}
          {blocked && (
            <Box
              component="span"
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                background: c.dangerText,
                color: c.white,
                borderRadius: `${brainTokens.radius.pill}px`,
                px: 0.85,
                py: 0.2,
              }}
            >
              Blocked
            </Box>
          )}
          <Box
            component="span"
            sx={{
              fontSize: 12.5,
              fontWeight: 500,
              color: c.faint,
              fontFamily: brainTokens.font.mono,
              ml: 0.5,
            }}
          >
            {openCount} open · {rows.length}
          </Box>
        </Box>
        <Box component="i" className={`ti ti-chevron-${open ? "up" : "down"}`} aria-hidden sx={{ color: c.faint, fontSize: 18 }} />
      </Box>

      {open && (
        <>
          <ColHeader showProjectCol={showProjectCol} />
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {rows.map((a, i) => {
              const st = deriveActionStatus(a);
              const done = a.status === "done" || a.status === "dismissed";
              const n = startIndex + i;
              const pri = priorityLook(a.priority);
              const statusValue =
                a.status === "dismissed"
                  ? "Dismiss"
                  : a.status === "done"
                    ? "Done"
                    : a.clientStatus
                      ? String(a.clientStatus)
                      : a.jiraIssueKey
                        ? "Raised"
                        : "Not raised";
              const ticket = a.jiraIssueKey || a.customerRef || "";
              const endRef = a.jiraIssueKey && a.customerRef ? a.customerRef : "";

              return (
                <Box
                  key={a.id}
                  sx={{
                    borderRadius: "4px",
                    background: c.pageBg,
                    overflow: "hidden",
                    borderBottom: `1px solid ${c.borderLight2}`,
                    borderLeft: `3px solid ${done ? c.border : pri.spine}`,
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: showProjectCol ? ROW_GRID_PROJECT : ROW_GRID,
                      gap: "14px",
                      alignItems: "center",
                      p: 1.5,
                      "&:hover": { background: c.canvas },
                      "@media (max-width: 900px)": {
                        gridTemplateColumns: "20px 74px 100px minmax(0,1fr) 32px 92px",
                        "& .col-status, & .col-project": { display: "none" },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: c.captionFaint,
                        textAlign: "center",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {n}
                    </Box>

                    <Box onClick={(e) => e.stopPropagation()}>
                      {canEdit ? (
                        <InlineSelect
                          value={a.priority ?? "P3"}
                          width={86}
                          options={[
                            { value: "P1", label: "P1" },
                            { value: "P2", label: "P2" },
                            { value: "P3", label: "P3" },
                          ]}
                          renderValue={(v) => <PriorityBadge priority={v} />}
                          onChange={(v) => onPatch(a.id, { priority: v }, { priority: v })}
                        />
                      ) : (
                        <PriorityBadge priority={a.priority} />
                      )}
                    </Box>

                    <Box onClick={(e) => e.stopPropagation()} sx={{ minWidth: 0 }}>
                      {canEdit ? (
                        <TextField
                          size="small"
                          defaultValue={a.jiraIssueKey ?? a.customerRef ?? ""}
                          placeholder="ANX-… / URL"
                          onBlur={(e) => {
                            const raw = e.target.value.trim();
                            const key = parseJiraKey(raw);
                            // Prefer Jira key when it parses; otherwise leave jira alone if user cleared.
                            if (key && /-\d+$/.test(key)) {
                              const prev = a.jiraIssueKey ?? "";
                              if (key !== prev) onPatch(a.id, { jiraIssueKey: key }, { jiraIssueKey: key });
                              return;
                            }
                            if (!raw && a.jiraIssueKey) {
                              onPatch(a.id, { jiraIssueKey: undefined }, { jiraIssueKey: undefined });
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                          }}
                          sx={{
                            width: "100%",
                            "& .MuiInputBase-input": {
                              fontFamily: brainTokens.font.mono,
                              fontSize: 12.5,
                              fontWeight: 600,
                              color: c.ink4,
                              py: 0.35,
                              px: 0.5,
                            },
                            "& fieldset": { borderColor: "transparent" },
                            "&:hover fieldset": { borderColor: c.border },
                          }}
                        />
                      ) : ticket ? (
                        a.jiraIssueKey ? (
                          <Box
                            component="a"
                            href={`${JIRA_BROWSE}/${encodeURIComponent(a.jiraIssueKey)}`}
                            target="_blank"
                            rel="noreferrer"
                            sx={{
                              fontFamily: brainTokens.font.mono,
                              fontSize: 12.5,
                              fontWeight: 600,
                              color: c.ink4,
                              textDecoration: "none",
                              whiteSpace: "nowrap",
                              "&:hover": { textDecoration: "underline", color: c.teal },
                            }}
                          >
                            {a.jiraIssueKey}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              fontFamily: brainTokens.font.mono,
                              fontSize: 12.5,
                              fontWeight: 600,
                              color: c.ink4,
                            }}
                          >
                            {ticket}
                          </Box>
                        )
                      ) : (
                        <Box sx={{ color: c.placeholder }}>—</Box>
                      )}
                    </Box>

                    <Box
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 500,
                        color: done ? c.muted : c.ink,
                        letterSpacing: "-0.01em",
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textDecoration: done ? "line-through" : "none",
                        textDecorationColor: c.border,
                      }}
                      title={a.text}
                    >
                      {a.text}
                    </Box>

                    {showProjectCol && (
                      <Box
                        className="col-project"
                        sx={{
                          fontFamily: brainTokens.font.mono,
                          fontSize: 12,
                          color: c.muted,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {a.projectId ?? "—"}
                      </Box>
                    )}

                    <Box className="col-status" onClick={(e) => e.stopPropagation()} sx={{ minWidth: 0 }}>
                      {canEdit ? (
                        <InlineSelect
                          value={statusValue}
                          width={150}
                          options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
                          renderValue={() => (
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: "50%", flex: "none", background: statusDotColor(st.tone) }} />
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: c.ink2,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {st.label}
                              </Box>
                            </Box>
                          )}
                          onChange={(choice) => {
                            const patch = applyStatusChoice(a, choice);
                            onPatch(a.id, patch, {
                              status: (patch.status as ProjectAction["status"]) ?? a.status,
                              clientStatus: (patch.clientStatus as string | undefined) ?? undefined,
                            });
                          }}
                        />
                      ) : (
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", flex: "none", background: statusDotColor(st.tone) }} />
                          <Box
                            component="span"
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: c.ink2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {st.label}
                          </Box>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
                      {canEdit ? (
                        <InlineSelect
                          value={a.assignee ?? ""}
                          width={44}
                          options={assigneeOpts}
                          renderValue={(v) => (v ? <PersonAvatar name={v} size={20} /> : <Box sx={{ color: c.placeholder, fontSize: 12 }}>—</Box>)}
                          onChange={(v) => onPatch(a.id, { assignee: v || null }, { assignee: v || undefined })}
                        />
                      ) : a.assignee ? (
                        <Box title={a.assignee}>
                          <PersonAvatar name={a.assignee} size={20} />
                        </Box>
                      ) : null}
                    </Box>

                    <Box
                      sx={{
                        justifySelf: "end",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {endRef && (
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: "10px",
                            py: "5px",
                            borderRadius: "8px",
                            fontSize: 12,
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            background: c.canvas,
                            color: c.muted,
                            fontFamily: brainTokens.font.mono,
                          }}
                        >
                          {endRef}
                        </Box>
                      )}
                      <Box component="i" className="ti ti-chevron-down" aria-hidden sx={{ color: c.faint, fontSize: 16, lineHeight: 1 }} />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
}

/**
 * Workstream-banded action register — shared by standup focus cards and project Actions tab.
 * Each workstream is a "Today's priorities"-style card (title · column headers · ranked rows).
 */
export function ActionRegisterTable({
  projectId,
  actions,
  workStreams = [],
  defaultGroupsOpen = false,
  nzDate = "",
  people = [],
  blockedStreams = [],
  canQuickAdd = true,
  canEdit = true,
  showProjectCol = false,
  defaultReleaseVersion,
  onActionsChange,
  onRowClick,
}: {
  projectId?: string;
  actions: ProjectAction[];
  workStreams?: string[];
  defaultGroupsOpen?: boolean;
  /** @deprecated QuickAddRow leftover; unused by AddWorkBar. */
  nzDate?: string;
  people?: ActionRegisterPerson[];
  blockedStreams?: string[];
  canQuickAdd?: boolean;
  canEdit?: boolean;
  showProjectCol?: boolean;
  /** @deprecated QuickAddRow leftover; unused by AddWorkBar. */
  defaultReleaseVersion?: string;
  onActionsChange?: (next: ProjectAction[]) => void;
  /** @deprecated prefer inline edits; kept for dialog open from Actions tab */
  onRowClick?: (a: ProjectAction) => void;
}) {
  void onRowClick;
  void nzDate;
  void defaultReleaseVersion;
  const { enqueueSnackbar } = useSnackbar();
  const [filterAssignee, setFilterAssignee] = useState<string | null>(null);
  const [filterPriority, setFilterPriority] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [workView, setWorkView] = useState<"open" | "done" | "all">("open");

  const actionsRef = useRef(actions);
  useEffect(() => {
    actionsRef.current = actions;
  }, [actions]);

  const publish = (next: ProjectAction[]) => {
    actionsRef.current = next;
    onActionsChange?.(next);
  };

  const assignees = useMemo(() => {
    const set = new Set<string>();
    for (const a of actions) {
      if (a.status === "dismissed") continue;
      const who = a.assignee?.trim();
      if (who) set.add(who);
    }
    return [...set].sort((x, y) => x.localeCompare(y));
  }, [actions]);

  const filtered = useMemo(() => {
    return actions.filter((a) => {
      const closed = a.status === "done" || a.status === "dismissed";
      if (workView === "open" && closed) return false;
      if (workView === "done" && !closed) return false;
      if (filterAssignee && (a.assignee?.trim() || "") !== filterAssignee) return false;
      if (filterPriority && (a.priority || "").toUpperCase() !== filterPriority.toUpperCase()) return false;
      if (filterStatus) {
        const st = deriveActionStatus(a).label;
        // Dropdown offers "Dismiss"; derive label is "Dismissed".
        if (filterStatus === "Dismiss") {
          if (a.status !== "dismissed") return false;
        } else if (st !== filterStatus) {
          return false;
        }
      }
      return true;
    });
  }, [actions, filterAssignee, filterPriority, filterStatus, workView]);

  const groups = useMemo(
    () => groupActionsByWorkStream(filtered, workStreams),
    [filtered, workStreams],
  );
  const blocked = useMemo(() => new Set(blockedStreams), [blockedStreams]);

  const appendCreated = (r: AddWorkCreated) => {
    // Brain-actions table has no Jira issue list — Jira create still toasts via AddWorkBar.
    if (r.kind === "jira") return;
    publish([...actionsRef.current, r.action]);
  };

  const onPatch = async (
    id: string,
    patch: Parameters<typeof updateProjectAction>[1],
    optimistic: Partial<ProjectAction>,
  ) => {
    const prev = actionsRef.current;
    publish(prev.map((a) => (a.id === id ? { ...a, ...optimistic } : a)));
    try {
      const updated = await updateProjectAction(id, patch);
      publish(actionsRef.current.map((a) => (a.id === id ? updated : a)));
    } catch (e) {
      publish(prev);
      enqueueSnackbar(e instanceof Error ? e.message : "Update failed.", { variant: "error" });
    }
  };

  const stop = (e: MouseEvent) => e.stopPropagation();

  const addBar =
    canQuickAdd && projectId ? (
      <Box sx={{ mt: 1.25 }}>
        <AddWorkBar
          scope="register"
          projectId={projectId}
          projects={[{ id: projectId, name: projectId }]}
          onCreated={appendCreated}
        />
      </Box>
    ) : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }} onClick={stop}>
        <Box sx={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: c.muted, mr: 0.5 }}>
          Filter
        </Box>
        <Segmented
          value={workView}
          onChange={setWorkView}
          options={[
            { key: "open", label: "Open" },
            { key: "done", label: "Done" },
            { key: "all", label: "All" },
          ]}
        />
        <Box sx={{ display: "inline-flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
          <Box
            component="button"
            type="button"
            onClick={() => setFilterAssignee(null)}
            sx={{
              border: `1px solid ${filterAssignee == null ? c.teal : c.border}`,
              background: filterAssignee == null ? c.tealTint : c.pageBg,
              color: filterAssignee == null ? c.tealInk : c.muted,
              borderRadius: `${brainTokens.radius.pill}px`,
              px: 1,
              py: 0.35,
              fontSize: 12,
              fontWeight: 650,
              cursor: "pointer",
              fontFamily: brainTokens.font.sans,
            }}
          >
            All
          </Box>
          {assignees.map((who) => (
            <Box
              key={who}
              component="button"
              type="button"
              title={who}
              onClick={() => setFilterAssignee((cur) => (cur === who ? null : who))}
              sx={{
                border: `2px solid ${filterAssignee === who ? c.teal : "transparent"}`,
                borderRadius: "50%",
                p: 0,
                background: "none",
                cursor: "pointer",
                lineHeight: 0,
              }}
            >
              <PersonAvatar name={who} size={28} />
            </Box>
          ))}
        </Box>
        <TextField
          select
          size="small"
          label="Priority"
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          sx={{ width: 110 }}
        >
          <MenuItem value="">All</MenuItem>
          {["P1", "P2", "P3"].map((p) => (
            <MenuItem key={p} value={p}>{p}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          sx={{ width: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>
      </Box>

      {groups.length === 0 ? (
        <Box
          sx={{
            background: c.pageBg,
            border: `1px solid ${c.border}`,
            borderRadius: `${brainTokens.radius.card}px`,
            p: "20px 23px",
            fontSize: 13,
            color: c.muted,
          }}
        >
          {actions.length === 0 ? "No actions yet." : "No actions match these filters."}
        </Box>
      ) : (
        (() => {
          let cursor = 1;
          return groups.map(({ stream, rows }) => {
            const start = cursor;
            cursor += rows.length;
            return (
              <WorkStreamCard
                key={stream}
                stream={stream}
                rows={rows}
                startIndex={start}
                defaultOpen={defaultGroupsOpen}
                blocked={blocked.has(stream)}
                people={people}
                canEdit={canEdit}
                onPatch={onPatch}
                showProjectCol={showProjectCol}
              />
            );
          });
        })()
      )}
      {addBar}
    </Box>
  );
}
